// ─── the client-placed provider seam ─────────────────────────────────────────
// When `session.ready.placement === 'client'` the browser owns the model
// connection: audio never leaves the machine, and the Durable Object stays the
// session's source of truth by being *told* what happened — transcripts, tool
// calls, turn boundaries (docs/SERVICE_API.md §2). This file is the seam; one
// driver per provider id, mirroring `server/src/providers/` on the other side.
//
// A server-placed session constructs none of this.

import type { ClientEvent, ClientProviderConfig, Role } from '@shared/protocol.ts'

/** A tool result in the shape both directions of the protocol use. */
export interface ToolOutcome {
  ok: boolean
  result?: unknown
  error?: string
}

/** What a driver is allowed to touch. Everything else is the app's business. */
export interface DriverHost {
  /** Send a client event up the socket — how the session stays authoritative. */
  emit(ev: ClientEvent): void
  /** Play assistant audio locally. In client placement it must never go up the socket. */
  play(audio: ArrayBuffer): void
  /**
   * Run a tool the model asked for, wherever it lives: locally if the session
   * declared it `runAt: 'client'`, otherwise round-tripped through the session.
   */
  runTool(name: string, args: unknown): Promise<ToolOutcome>
  /** Tool schemas to offer the model, in OpenAI-neutral shape. */
  tools(): Array<{ name: string; description: string; parameters: Record<string, unknown> }>
  /** Injected so the driver is testable without a browser or a model. */
  fetch: typeof fetch
  /** A failure the user must see. A dead local endpoint is the common case. */
  fail(message: string): void
  log(line: string): void
}

export interface ClientDriver {
  readonly id: string
  /** Greet / prime the model. Called once, after `session.ready`. */
  start(): Promise<void>
  /** A frame of mic audio in the session's input format. */
  pushAudio(pcm: ArrayBuffer): void
  /** The user's turn ended: run the model. */
  commit(): void
  /** `say` arrived from outside the audio stream (`POST /v1/session/:id/say`). */
  say(text: string, role: Role): void
  /** Barge-in: stop generating and drop anything pending. */
  cancel(): void
  close(): void
}

export type DriverFactory = (config: ClientProviderConfig, host: DriverHost, opts: DriverOptions) => ClientDriver

export interface DriverOptions {
  /** Spoken on `start`; the client owns the greeting in this placement. */
  greeting?: string
}

/** Raised when `clientConfig` does not carry what the driver needs to run. */
export class DriverConfigError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'DriverConfigError'
  }
}
